/**
 * Image caching service using IndexedDB
 * Caches images with their blurhash as version identifier or TTL-based expiration
 */

import {
  getMetadata,
  StorageError,
  type StorageReference,
} from "firebase/storage";
import type {
  CacheInvalidationCallback,
  CachedImage,
  CachedImageResult,
} from "@/types/cache";
import { CacheTypes } from "@/enums/cache-types";
import { SHORT_MEDIA_CACHE_MAX_BYTES } from "@/constants/shorts";

const DB_NAME = "kekkle-image-cache";
const DB_VERSION = 1;
const STORE_NAME = "images";

let dbPromise: Promise<IDBDatabase> | null = null;

// Cache invalidation callback registry
const invalidationCallbacks: CacheInvalidationCallback[] = [];

/**
 * Register a callback to be notified when cache is invalidated
 * @param callback - Function to call when cache is cleared
 */
export function onCacheInvalidated(callback: CacheInvalidationCallback): void {
  invalidationCallbacks.push(callback);
}

/**
 * Initialize or get the IndexedDB connection
 */
function getDB(): Promise<IDBDatabase> {
  if (dbPromise) {
    return dbPromise;
  }

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Create object store if it doesn't exist
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const objectStore = db.createObjectStore(STORE_NAME, {
          keyPath: "key",
        });
        // Index by timestamp for cleanup
        objectStore.createIndex("timestamp", "timestamp", { unique: false });
      }
    };
  });

  return dbPromise;
}

/**
 * Updates lastAccessed timestamp on an entry for LRU tracking.
 */
function touchCacheEntry(key: string): void {
  getDB()
    .then((db) => {
      const transaction = db.transaction(STORE_NAME, "readwrite");
      const store = transaction.objectStore(STORE_NAME);
      const req = store.get(key);
      req.onsuccess = () => {
        const item = req.result as CachedImage | undefined;
        if (item) {
          item.lastAccessed = Date.now();
          store.put(item);
        }
      };
    })
    .catch(() => {});
}

/**
 * Prunes cached short videos according to a maximum byte limit and LRU order.
 * Sorts by lastAccessed (fallback to timestamp) ascending (oldest first).
 */
export async function pruneShortVideoCache(
  maxBytes: number = SHORT_MEDIA_CACHE_MAX_BYTES,
  incomingBytes: number = 0,
): Promise<void> {
  try {
    const db = await getDB();
    const transaction = db.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    const request = store.getAll();

    return new Promise((resolve) => {
      request.onsuccess = () => {
        const allItems = request.result as CachedImage[];
        const items = allItems.filter(
          (item) =>
            item.key && item.key.startsWith(`${CacheTypes.ShortVideo}:`),
        );

        let totalBytes = items.reduce(
          (acc, item) => acc + (item.blob?.size || item.size || 0),
          0,
        );

        if (totalBytes + incomingBytes <= maxBytes) {
          resolve();
          return;
        }

        // Sort by lastAccessed (fallback to timestamp) ascending -> oldest first
        items.sort((a, b) => {
          const timeA = a.lastAccessed ?? a.timestamp;
          const timeB = b.lastAccessed ?? b.timestamp;
          return timeA - timeB;
        });

        for (const item of items) {
          if (totalBytes + incomingBytes <= maxBytes) {
            break;
          }
          store.delete(item.key);
          totalBytes -= item.blob?.size || item.size || 0;
        }

        transaction.oncomplete = () => resolve();
        transaction.onerror = () => resolve();
      };

      request.onerror = () => resolve();
    });
  } catch (error) {
    console.error("Error pruning short video cache:", error);
  }
}

/**
 * Emergency prune when IndexedDB throws QuotaExceededError.
 * Prunes the oldest 50% of short videos to immediately recover space.
 */
export async function emergencyPruneShortVideos(): Promise<void> {
  try {
    const db = await getDB();
    const transaction = db.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    const request = store.getAll();

    return new Promise((resolve) => {
      request.onsuccess = () => {
        const allItems = request.result as CachedImage[];
        const items = allItems.filter(
          (item) =>
            item.key && item.key.startsWith(`${CacheTypes.ShortVideo}:`),
        );

        if (items.length === 0) {
          resolve();
          return;
        }

        items.sort((a, b) => {
          const timeA = a.lastAccessed ?? a.timestamp;
          const timeB = b.lastAccessed ?? b.timestamp;
          return timeA - timeB;
        });

        // Delete oldest half
        const deleteCount = Math.max(1, Math.ceil(items.length / 2));
        for (let i = 0; i < deleteCount; i++) {
          store.delete(items[i]!.key);
        }

        transaction.oncomplete = () => resolve();
        transaction.onerror = () => resolve();
      };

      request.onerror = () => resolve();
    });
  } catch (error) {
    console.error("Error during emergency prune of short videos:", error);
  }
}

/**
 * Generate a cache key for an image
 * @param type - Type of image
 * @param id - ID of the resource
 * @param format - Image format suffix
 * @returns Cache key
 */
export function generateKey(type: string, id: string, format: string): string {
  return `${type}:${id}:${format}`;
}

/**
 * Validates MD5 hash in background and invalidates cache if mismatch
 * @param storageReference - Firebase Storage reference
 * @param key - Cache key
 * @param cachedMd5 - MD5 hash from cache
 */
async function validateAndInvalidateCache(
  storageReference: StorageReference,
  key: string,
  cachedMd5: string,
): Promise<void> {
  try {
    const metadata = await getMetadata(storageReference);
    const currentMd5 = metadata.md5Hash;

    // If MD5 hash has changed, invalidate cache for ALL sizes
    if (currentMd5 && currentMd5 !== cachedMd5) {
      console.log(
        `MD5 hash mismatch for ${key}. Cached: ${cachedMd5}, Current: ${currentMd5}`,
      );

      // Extract base pattern (everything except the format suffix)
      // Key format: "type:id:format" -> we want "type:id"
      const lastColonIndex = key.lastIndexOf(":");
      const basePattern =
        lastColonIndex !== -1 ? key.substring(0, lastColonIndex) : key;

      console.log(`Invalidating all cached sizes for pattern: ${basePattern}`);

      // Clear all sizes for this image
      await clearCachedImages(basePattern);
      invalidationCallbacks.forEach((callback) => callback(basePattern));
    }
  } catch (error) {
    if (error instanceof StorageError) {
      if (error.code === "storage/object-not-found") {
        // The source image no longer exists in Storage. Drop every cached size
        // and notify subscribers so in-memory caches evict it too - otherwise
        // they'd keep serving a blob whose source is gone until the next reload.

        // Key format: "type:id:format" -> we want "type:id"
        const lastColonIndex = key.lastIndexOf(":");
        const basePattern =
          lastColonIndex !== -1 ? key.substring(0, lastColonIndex) : key;
        console.log("removing from cache");
        await clearCachedImages(basePattern);
        invalidationCallbacks.forEach((callback) => callback(basePattern));
      }
    }
  }
}

/**
 * Get a cached image by key
 *
 * MD5 Validation Flow:
 * 1. Returns cached image immediately for instant display
 * 2. Validates MD5 hash in background (non-blocking)
 * 3. If mismatch detected, clears ALL sizes from cache
 * 4. Next fetch will get fresh image from Firebase Storage
 *
 * @param key - Cache key
 * @param storageReference - Firebase Storage reference for MD5 validation (optional)
 * @param callerTtlMs - Time to live in milliseconds requested by caller (optional, for TTL-based validation)
 * @returns Cached image with blob and URL, or null if not found/expired
 */
export async function getCachedImage(
  key: string,
  storageReference: StorageReference | null = null,
  callerTtlMs: number | null = null,
): Promise<CachedImageResult | null> {
  try {
    const db = await getDB();
    const transaction = db.transaction(STORE_NAME, "readonly");
    const store = transaction.objectStore(STORE_NAME);

    const request = store.get(key);

    return new Promise((resolve) => {
      request.onsuccess = () => {
        const result = request.result as CachedImage | undefined;

        if (!result) {
          resolve(null);
          return;
        }

        // Validate blob integrity - discard corrupted or zero-byte blobs
        if (!result.blob || result.blob.size === 0) {
          console.warn(
            `Corrupted 0-byte blob in cache for ${key}, deleting entry`,
          );
          const deleteTransaction = db.transaction(STORE_NAME, "readwrite");
          const deleteStore = deleteTransaction.objectStore(STORE_NAME);
          deleteStore.delete(key);
          resolve(null);
          return;
        }

        // Ensure proper MIME type is preserved, particularly for iOS WebKit video playback
        const expectedMimeType =
          result.mimeType ||
          (key.startsWith(`${CacheTypes.ShortVideo}:`)
            ? "video/mp4"
            : result.blob.type || "image/jpeg");

        const finalBlob =
          result.blob.type === expectedMimeType
            ? result.blob
            : new Blob([result.blob], { type: expectedMimeType });

        // MD5 hash validation (if storage reference provided)
        // Run validation asynchronously without blocking the cache return
        if (storageReference && result.md5Hash) {
          // Start validation in background (don't await)
          validateAndInvalidateCache(
            storageReference,
            key,
            result.md5Hash,
          ).catch((error) => {
            console.error(
              `Background MD5 validation failed for ${key}:`,
              error,
            );
          });
        }

        // TTL-based validation
        // Priority: setter TTL > caller TTL
        const age = Date.now() - result.timestamp;

        // First check setter TTL (if image was cached with TTL)
        if (result.ttlMs !== null) {
          if (age < result.ttlMs) {
            touchCacheEntry(key);
            resolve({
              blob: finalBlob,
              url: URL.createObjectURL(finalBlob),
            });
          } else {
            // Setter TTL expired - delete from cache
            console.log(
              `Setter TTL expired for ${key}. Age: ${age}ms, TTL: ${result.ttlMs}ms`,
            );
            const deleteTransaction = db.transaction(STORE_NAME, "readwrite");
            const deleteStore = deleteTransaction.objectStore(STORE_NAME);
            deleteStore.delete(key);
            resolve(null);
          }
          return;
        }

        // Then check caller TTL (if provided)
        if (callerTtlMs !== null) {
          if (age < callerTtlMs) {
            touchCacheEntry(key);
            resolve({
              blob: finalBlob,
              url: URL.createObjectURL(finalBlob),
            });
          } else {
            // Caller TTL expired - return null but keep in cache
            // The caller doesn't want to use this cached version, but other callers might
            console.log(
              `Caller TTL expired for ${key}. Age: ${age}ms, Caller TTL: ${callerTtlMs}ms (cache entry preserved)`,
            );
            resolve(null);
          }
          return;
        }

        // No validation specified - return cached image
        touchCacheEntry(key);
        resolve({
          blob: finalBlob,
          url: URL.createObjectURL(finalBlob),
        });
      };

      request.onerror = () => {
        console.error("Error reading from cache:", request.error);
        resolve(null);
      };
    });
  } catch (error) {
    console.error("Error accessing cache:", error);
    return null;
  }
}

/**
 * Store an image in the cache
 * @param key - Cache key
 * @param blob - Image blob
 * @param ttlMs - Time to live in milliseconds (optional, for TTL-based caching)
 * @param md5Hash - MD5 hash for cache validation (optional)
 * @returns Promise that resolves when the image is cached
 */
export async function setCachedImage(
  key: string,
  blob: Blob,
  ttlMs: number | null = null,
  md5Hash: string | null = null,
): Promise<void> {
  try {
    // If storing a short video, ensure we stay within the 200MB budget first
    if (key.startsWith(`${CacheTypes.ShortVideo}:`)) {
      await pruneShortVideoCache(SHORT_MEDIA_CACHE_MAX_BYTES, blob.size);
    }

    const db = await getDB();
    const now = Date.now();
    const mimeType =
      blob.type ||
      (key.startsWith(`${CacheTypes.ShortVideo}:`)
        ? "video/mp4"
        : "image/jpeg");
    const safeBlob =
      blob.type === mimeType ? blob : new Blob([blob], { type: mimeType });

    const data: CachedImage = {
      key,
      blob: safeBlob,
      timestamp: now,
      ttlMs,
      md5Hash,
      lastAccessed: now,
      size: safeBlob.size,
      mimeType,
    };

    const putEntry = (database: IDBDatabase): Promise<void> => {
      const transaction = database.transaction(STORE_NAME, "readwrite");
      const store = transaction.objectStore(STORE_NAME);
      store.put(data);

      return new Promise((resolve, reject) => {
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => reject(transaction.error);
      });
    };

    try {
      await putEntry(db);
    } catch (putError: any) {
      if (
        putError?.name === "QuotaExceededError" ||
        putError?.code === 22 ||
        putError?.name === "NS_ERROR_DOM_QUOTA_REACHED"
      ) {
        console.warn(
          "IndexedDB QuotaExceededError encountered. Performing emergency prune of short videos...",
        );
        await emergencyPruneShortVideos();
        // Retry once after emergency pruning
        await putEntry(db);
      } else {
        throw putError;
      }
    }
  } catch (error) {
    console.error("Error writing to cache:", error);
  }
}

/**
 * Clear cached images matching a key pattern
 * @param keyPattern - Pattern to match against cache keys (e.g., "user:123" will clear all "user:123:*")
 * @returns Promise that resolves when images are cleared
 */
export async function clearCachedImages(keyPattern: string): Promise<void> {
  try {
    const db = await getDB();
    const transaction = db.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);

    // Get all keys and delete matching ones
    const request = store.getAllKeys();

    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        const keys = request.result as string[];
        const matchingKeys = keys.filter((key) => key.includes(keyPattern));

        console.log(
          `Clearing ${matchingKeys.length} cached images matching pattern: ${keyPattern}`,
        );
        if (matchingKeys.length > 0) {
          console.log("Keys to delete:", matchingKeys);
        }

        matchingKeys.forEach((key) => {
          store.delete(key);
        });

        transaction.oncomplete = () => resolve();
        transaction.onerror = () => reject(transaction.error);
      };

      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error("Error clearing cache:", error);
  }
}

/**
 * Clear expired cached images based on their setter TTL
 * Should be called on app startup to prevent IndexedDB from growing too large
 * @returns Promise that resolves when expired images are cleared
 */
export async function clearOldCachedImages(): Promise<void> {
  try {
    const db = await getDB();
    const transaction = db.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);

    // Get all cached images
    const getAllRequest = store.getAll();

    return new Promise((resolve, reject) => {
      getAllRequest.onsuccess = () => {
        const items = getAllRequest.result as CachedImage[];
        const now = Date.now();
        let deletedCount = 0;

        items.forEach((item) => {
          // Only delete if setter TTL is set and has expired
          if (item.ttlMs !== null) {
            const age = now - item.timestamp;
            if (age >= item.ttlMs) {
              store.delete(item.key);
              deletedCount++;
            }
          }
        });

        transaction.oncomplete = () => {
          if (deletedCount > 0) {
            console.log(
              `Cache cleanup: Deleted ${deletedCount} expired image(s) from cache`,
            );
          }
          resolve();
        };
        transaction.onerror = () => reject(transaction.error);
      };

      getAllRequest.onerror = () => reject(getAllRequest.error);
    });
  } catch (error) {
    console.error("Error clearing old cache:", error);
  }
}

/**
 * Get cache statistics
 * @returns Cache statistics including count and size
 */
export async function getCacheStats(): Promise<{
  count: number;
  size: number;
  sizeFormatted: string;
}> {
  try {
    const db = await getDB();
    const transaction = db.transaction(STORE_NAME, "readonly");
    const store = transaction.objectStore(STORE_NAME);

    const countRequest = store.count();
    const allRequest = store.getAll();

    return new Promise((resolve) => {
      let count = 0;
      let size = 0;

      countRequest.onsuccess = () => {
        count = countRequest.result;
      };

      allRequest.onsuccess = () => {
        const items = allRequest.result as CachedImage[];
        items.forEach((item) => {
          size += item.blob.size;
        });

        resolve({
          count,
          size,
          sizeFormatted: formatBytes(size),
        });
      };

      countRequest.onerror = () =>
        resolve({ count: 0, size: 0, sizeFormatted: "0 Bytes" });
      allRequest.onerror = () =>
        resolve({ count: 0, size: 0, sizeFormatted: "0 Bytes" });
    });
  } catch (error) {
    console.error("Error getting cache stats:", error);
    return { count: 0, size: 0, sizeFormatted: "0 Bytes" };
  }
}

/**
 * Format bytes to human readable string
 */
function formatBytes(bytes: number): string {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
}

/**
 * Clear all cached images
 * @returns Promise that resolves when all images are cleared
 */
export async function clearAllCachedImages(): Promise<void> {
  try {
    const db = await getDB();
    const transaction = db.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);

    store.clear();

    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => {
        invalidationCallbacks.forEach((callback) => callback("*"));
        resolve();
      };
      transaction.onerror = () => reject(transaction.error);
    });
  } catch (error) {
    console.error("Error clearing all cache:", error);
  }
}

/** Aliases for media cache naming */
export const clearAllCachedMedia = clearAllCachedImages;
export const getMediaCacheStats = getCacheStats;
export const clearCachedMedia = clearCachedImages;
